import { animalSpeciesLabel } from '@tcc-mono/vandijk/shared/data-access/models';
import { RvoVerblijfPlaats } from './rvo-animal.models';

export type VerblijfAanvoerStatus = 'ok' | 'missing';
export type VerblijfAfvoerStatus = 'ok' | 'on_location' | 'missing';
export type AnimalLifeStatus = 'active' | 'slaughtered' | 'dead' | 'exported' | 'ended';

const BEDRIJFSTYPE_LABELS: Record<string, string> = {
  VH: 'Veehouder',
  VP: 'Verzamelplaats',
  SP: 'Slachtplaats',
  TR: 'Transporteur',
  AK: 'Artskoppel',
  IN: 'Inschakeling',
};

const GENDER_LABELS: Record<string, string> = {
  M: 'Mannelijk',
  V: 'Vrouwelijk',
  O: 'Onbekend',
};

const REDEN_EINDE_LABELS: Record<string, string> = {
  VM: 'Vermissing',
  GV: 'Gevonden',
  SL: 'Slacht',
  DO: 'Dood',
  ND: 'Overleden',
  EX: 'Export',
  AF: 'Afvoer',
  DV: 'Diervlag',
};

export function parseAnimalIdentification(
  countryCode: string,
  rawId: string,
): { country_code: string; identification_number: string } {
  const trimmedId = rawId.trim();
  const combined = trimmedId.match(/^([A-Za-z]{2})(\d+)$/);

  return {
    country_code: (combined?.[1] ?? countryCode).trim().toUpperCase(),
    identification_number: (combined?.[2] ?? trimmedId).trim(),
  };
}

export function rvoAnimalTypeLabel(code: string | null | undefined): string {
  return animalSpeciesLabel(code, '-');
}

export function rvoCompanyTypeLabel(code: string | null | undefined): string {
  return bedrijfstypeLabel(code);
}

export function speciesLabel(code: string | null | undefined): string {
  return rvoAnimalTypeLabel(code);
}

export function genderLabel(code: string | null | undefined): string {
  if (!code?.trim()) {
    return '—';
  }
  return GENDER_LABELS[code.trim().toUpperCase()] ?? code;
}

export function resolveAnimalLifeStatus(
  dierEinddatum: string | null | undefined,
  dierRedenEinde: string | null | undefined,
): AnimalLifeStatus {
  if (!animalHasEnded(dierEinddatum, dierRedenEinde)) {
    return 'active';
  }

  const reason = dierRedenEinde?.trim().toUpperCase();
  switch (reason) {
    case 'SL':
      return 'slaughtered';
    case 'ND':
    case 'DO':
      return 'dead';
    case 'EX':
      return 'exported';
    default:
      return 'ended';
  }
}

export function animalLifeStatusLabel(
  status: AnimalLifeStatus,
  dierRedenEinde?: string | null,
): string {
  if (dierRedenEinde?.trim()) {
    return redenEindeLabel(dierRedenEinde);
  }
  switch (status) {
    case 'slaughtered':
      return 'Slacht';
    case 'dead':
      return 'Dood';
    case 'exported':
      return 'Export';
    case 'ended':
      return 'Beëindigd in RVO';
    default:
      return '';
  }
}

export function animalLifeStatusClass(status: AnimalLifeStatus): string {
  switch (status) {
    case 'slaughtered':
      return 'life-status--slaughtered';
    case 'dead':
      return 'life-status--dead';
    case 'exported':
      return 'life-status--exported';
    case 'ended':
      return 'life-status--ended';
    default:
      return 'life-status--active';
  }
}

export function redenEindeLabel(code: string | null | undefined): string {
  if (!code?.trim()) {
    return 'Beëindigd in RVO';
  }
  const normalized = code.trim().toUpperCase();
  return REDEN_EINDE_LABELS[normalized] ?? code.trim();
}

export function animalHasEnded(
  dierEinddatum: string | null | undefined,
  dierRedenEinde: string | null | undefined,
): boolean {
  return hasRvoDate(dierEinddatum) || !!dierRedenEinde?.trim();
}

export function sortVerblijfPlaatsenNewestFirst(places: RvoVerblijfPlaats[]): RvoVerblijfPlaats[] {
  return [...places].sort(
    (a, b) => parseRvoDate(b.aanvoerDatumME) - parseRvoDate(a.aanvoerDatumME),
  );
}

export function sortVerblijfPlaatsen<T extends {
  aanvoerDatumME?: string | null;
  afvoerDatumME?: string | null;
}>(places: T[]): T[] {
  return [...places].sort((a, b) => {
    const aDate = parseDmy(a.aanvoerDatumME) ?? parseDmy(a.afvoerDatumME);
    const bDate = parseDmy(b.aanvoerDatumME) ?? parseDmy(b.afvoerDatumME);
    const aTs = aDate ? aDate.getTime() : 0;
    const bTs = bDate ? bDate.getTime() : 0;
    return bTs - aTs;
  });
}

export function aanvoerStatus(plaats: RvoVerblijfPlaats): VerblijfAanvoerStatus {
  return hasRvoDate(plaats.aanvoerDatumME) ? 'ok' : 'missing';
}

export function afvoerStatus(plaats: RvoVerblijfPlaats, indexNewestFirst: number): VerblijfAfvoerStatus {
  if (hasRvoDate(plaats.afvoerDatumME)) {
    return 'ok';
  }
  return indexNewestFirst === 0 ? 'on_location' : 'missing';
}

export interface MissingDestinationAanvoer {
  show: boolean;
  afterAfvoerDate: string | null;
  fromUbn: string | null;
}

/**
 * Nieuwste verblijf heeft afvoer maar geen vervolglocatie → aanvoer op bestemming ontbreekt.
 * Uitzonderingen: dier heeft einddatum (geslacht/dood) of laatste locatie is Slachtplaats (SP).
 * De bestemming zelf is onbekend: RVO heeft nog geen volgende verblijfplaats.
 */
export function missingDestinationAanvoer(
  places: RvoVerblijfPlaats[],
  dierEinddatum: string | null | undefined,
  dierRedenEinde?: string | null,
): MissingDestinationAanvoer {
  const empty: MissingDestinationAanvoer = {
    show: false,
    afterAfvoerDate: null,
    fromUbn: null,
  };

  const sorted = sortVerblijfPlaatsenNewestFirst(places);
  if (sorted.length === 0) {
    return empty;
  }

  const newest = sorted[0];

  if (animalHasEnded(dierEinddatum, dierRedenEinde) || isSlachtplaats(newest.typeBedrijfsvestiging)) {
    return empty;
  }

  if (!hasRvoDate(newest.afvoerDatumME)) {
    return empty;
  }

  return {
    show: true,
    afterAfvoerDate: newest.afvoerDatumME,
    fromUbn: newest.meldingeenheid?.trim() || null,
  };
}

export function isSlachtplaats(typeBedrijfsvestiging: string | null | undefined): boolean {
  return typeBedrijfsvestiging?.trim().toUpperCase() === 'SP';
}

export function bedrijfstypeLabel(code: string | null | undefined): string {
  if (!code?.trim()) {
    return '—';
  }
  return BEDRIJFSTYPE_LABELS[code.trim().toUpperCase()] ?? code;
}

export function formatVerblijfAddress(plaats: RvoVerblijfPlaats): string | null {
  const parts = [plaats.adresOmsME, plaats.postcodeOmsME, plaats.woonplaatsME].filter(
    (part) => !!part?.trim(),
  );
  return parts.length > 0 ? parts.join(', ') : null;
}

export function formatRvoDisplayDate(value: string | null | undefined): string {
  if (!hasRvoDate(value)) {
    return '';
  }
  const parsed = parseRvoDateParts(value!);
  if (!parsed) {
    return value!;
  }
  return new Intl.DateTimeFormat('nl-NL', { dateStyle: 'medium' }).format(
    new Date(parsed.year, parsed.month - 1, parsed.day),
  );
}

export function formatAnimalDisplayId(landcode: string | null, levensnummer: string | null): string {
  const lc = landcode?.trim() ?? '';
  const nr = levensnummer?.trim() ?? '';
  if (!lc && !nr) {
    return '—';
  }
  return `${lc}${nr}`.trim();
}

export function formatReplacedAnimalId(
  landcode: string | null | undefined,
  levensnummer: string | null | undefined,
  werknummer?: string | null | undefined,
): string | null {
  if (!levensnummer?.trim()) {
    return null;
  }

  const id = formatAnimalDisplayId(landcode ?? null, levensnummer);
  const wnr = werknummer?.trim();
  return wnr ? `${id} · wnr. ${wnr}` : id;
}

export function formatMotherAnimalId(
  landcode: string | null | undefined,
  levensnummer: string | null | undefined,
  werknummer?: string | null | undefined,
): string | null {
  return formatReplacedAnimalId(landcode, levensnummer, werknummer);
}

export function daysSinceDmyInclusive(dateDmy: string | null | undefined): number | null {
  const date = parseDmy(dateDmy);
  if (!date) {
    return null;
  }

  const today = new Date();
  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const startMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffMs = todayMidnight.getTime() - startMidnight.getTime();
  const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000)) + 1;

  return diffDays >= 1 ? diffDays : null;
}

export function parseDmy(dateDmy: string | null | undefined): Date | null {
  if (!dateDmy) {
    return null;
  }

  const parts = dateDmy.split('-');
  if (parts.length !== 3) {
    return null;
  }

  const [dd, mm, yyyy] = parts;
  const day = Number(dd);
  const month = Number(mm);
  const year = Number(yyyy);

  if (!Number.isFinite(day) || !Number.isFinite(month) || !Number.isFinite(year)) {
    return null;
  }

  if (day < 1 || day > 31 || month < 1 || month > 12 || year < 1900) {
    return null;
  }

  const dt = new Date(year, month - 1, day);
  if (dt.getFullYear() !== year || dt.getMonth() !== month - 1 || dt.getDate() !== day) {
    return null;
  }

  return dt;
}

function hasRvoDate(value: string | null | undefined): boolean {
  return !!value?.trim();
}

function parseRvoDate(value: string | null | undefined): number {
  const parts = parseRvoDateParts(value);
  if (!parts) {
    return 0;
  }
  return new Date(parts.year, parts.month - 1, parts.day).getTime();
}

function parseRvoDateParts(value: string | null | undefined): { day: number; month: number; year: number } | null {
  if (!value?.trim()) {
    return null;
  }
  const match = value.trim().match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (!match) {
    return null;
  }
  return {
    day: Number(match[1]),
    month: Number(match[2]),
    year: Number(match[3]),
  };
}
